/** PackBytes' own WhatsApp number and mail server, and the messages PackBytes sends to shops. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '../../api/client';

export type PlatformWhatsapp = Schemas['PackbytesWhatsapp'];
export type PlatformMail = Schemas['PlatformMail'];
export type PlatformMessage = Schemas['PlatformMessage'];
export type SendTenantMessage = Schemas['SendTenantMessageRequest'];
export type Channel = SendTenantMessage['channel'];

export const messagingKeys = {
  whatsapp: ['platform', 'settings', 'whatsapp'] as const,
  mail: ['platform', 'settings', 'mail'] as const,
  tenant: (id: string) => ['platform', 'tenants', id, 'messages'] as const,
};

/** PackBytes' WhatsApp; `poll` while linking (the QR changes; stops once linked). */
export function usePlatformWhatsapp(poll = false) {
  return useQuery({
    queryKey: messagingKeys.whatsapp,
    queryFn: () => unwrap(api.GET('/platform/settings/whatsapp')),
    refetchInterval: (q) => (poll && q.state.data?.status !== 'LINKED' ? 3_000 : false),
  });
}

export function usePlatformMail() {
  return useQuery({
    queryKey: messagingKeys.mail,
    queryFn: () => unwrap(api.GET('/platform/settings/mail')),
  });
}

export function usePlatformWhatsappActions() {
  const qc = useQueryClient();
  const set = (data: PlatformWhatsapp) => qc.setQueryData(messagingKeys.whatsapp, data);
  return {
    connect: useMutation({
      mutationFn: () => unwrap(api.POST('/platform/settings/whatsapp/connect')),
      onSuccess: set,
    }),
    disconnect: useMutation({
      mutationFn: () => unwrap(api.POST('/platform/settings/whatsapp/disconnect')),
      onSuccess: set,
    }),
    test: useMutation({
      mutationFn: (to: string) =>
        unwrap(api.POST('/platform/settings/whatsapp/test', { body: { to } })),
    }),
  };
}

export function useTenantMessages(tenantId: string) {
  return useQuery({
    queryKey: messagingKeys.tenant(tenantId),
    queryFn: () =>
      unwrap(api.GET('/platform/tenants/{id}/messages', { params: { path: { id: tenantId } } })),
  });
}

export function useSendTenantMessage(tenantId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SendTenantMessage) =>
      unwrap(
        api.POST('/platform/tenants/{id}/messages', { params: { path: { id: tenantId } }, body }),
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: messagingKeys.tenant(tenantId) }),
  });
}
